import { useParams, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Download, History, ChevronDown, ChevronUp } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { LEGAL_DOCS, LEGAL_LINKS, LEGAL_CHANGELOG } from "../lib/legal";
import { SITE } from "../lib/site";
import TripAutoHero from "../components/TripAutoHero";

const HERO_QUERY = {
  terms: "law book library contract",
  privacy: "privacy lock secure travel documents",
};

const MD = {
  h2: (p) => <h2 className="font-heading font-900 text-xl sm:text-2xl mt-8 mb-3 scroll-mt-24" {...p} />,
  h3: (p) => <h3 className="font-heading font-700 text-lg mt-4 mb-1" {...p} />,
  p: (p) => <p className="text-base leading-relaxed text-zinc-700 mb-3" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 space-y-1 text-base text-zinc-700 mb-3" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 space-y-1 text-base text-zinc-700 mb-3" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  strong: (p) => <strong className="font-700 text-zinc-900" {...p} />,
  a: (p) => <a className="text-primary hover:underline" {...p} />,
};

function LegalNav({ current }) {
  return (
    <nav className="flex flex-wrap items-center gap-x-1 gap-y-2 text-sm print:hidden" data-testid="legal-nav">
      {LEGAL_LINKS.map(([label, to], i) => {
        const active = to.endsWith(`/${current}`);
        return (
          <span key={to} className="inline-flex items-center">
            {i > 0 && <span className="mx-2 text-zinc-300">|</span>}
            <Link to={to} data-testid={`legal-link-${to.split("/").pop()}`}
              className={active ? "font-700 text-primary" : "text-zinc-600 hover:text-primary transition-colors"}>{label}</Link>
          </span>
        );
      })}
    </nav>
  );
}

function Changelog({ slug, changelog }) {
  const [expanded, setExpanded] = useState(false);
  const sections = changelog.sections || [];
  return (
    <div
      className="mt-6 border-l-4 border-[#0e7490] bg-cyan-50/60 px-4 py-3 text-sm"
      data-testid={`legal-changelog-${slug}`}
    >
      <div className="flex gap-3">
        <History className="w-5 h-5 shrink-0 mt-0.5 text-[#0e7490]" />
        <div className="flex-1">
          <div className="font-700 text-zinc-900 mb-0.5">What changed on {changelog.date}</div>
          <p className="text-zinc-700 leading-relaxed">{changelog.summary}</p>
          {sections.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-primary font-700 text-sm hover:underline print:hidden"
              data-testid={`legal-changelog-toggle-${slug}`}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {expanded ? "Hide section-by-section changes" : "See section-by-section changes"}
            </button>
          )}
          {expanded && (
            <ul className="mt-3 space-y-2 border-t border-cyan-200/70 pt-3" data-testid={`legal-changelog-diff-${slug}`}>
              {sections.map((s, i) => (
                <li key={i} className="text-sm">
                  <div className="font-700 text-zinc-900">{s.heading}</div>
                  <p className="text-zinc-700 leading-relaxed">{s.change}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Legal() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const doc = LEGAL_DOCS[slug];
  const changelog = LEGAL_CHANGELOG[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  if (!doc) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <p className="text-muted-foreground mb-4">This document could not be found.</p>
        <LegalNav current={slug} />
      </div>
    );
  }

  const handlePrint = () => {
    document.title = `${SITE.brandLabel} — ${doc.title} (effective ${doc.effective})`;
    window.print();
  };

  return (
    <div data-testid="legal-page" className="legal-print-root">
      <div className="relative border-b border-zinc-200 overflow-hidden print:hidden">
        <TripAutoHero query={HERO_QUERY[slug] || "legal document travel"} alt={doc.title}
          className="absolute inset-0 w-full h-full" testid={`legal-hero-${slug}`} />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/90 via-slate-800/75 to-cyan-700/55" />
        <div className="relative max-w-3xl mx-auto px-5 lg:px-8 py-14">
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-200 mb-2">{SITE.brandLabel} · Legal</div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight text-white">{doc.title}</h1>
              <p className="mt-3 text-sm text-white/80 font-mono">Effective: {doc.effective} · Last updated: {doc.updated}</p>
            </div>
            <button
              onClick={handlePrint}
              data-testid={`legal-download-pdf-${slug}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/30 text-sm font-700 text-white hover:bg-white/20 transition-colors"
              title="Save as PDF (use your browser's Save as PDF option)"
            >
              <Download className="w-4 h-4" /> Download PDF
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-10">
        <div className="hidden print:block mb-6">
          <div className="text-xs font-mono uppercase tracking-widest text-primary mb-2">{SITE.brandLabel}</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight">{doc.title}</h1>
          <p className="mt-2 text-sm text-muted-foreground font-mono">Effective: {doc.effective} · Last updated: {doc.updated}</p>
        </div>

        {changelog && <Changelog slug={slug} changelog={changelog} />}

        <div className="mt-2 pb-6 border-b border-zinc-200"><LegalNav current={slug} /></div>

        <div className="mt-8">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{doc.md}</ReactMarkdown>
        </div>

        <div className="mt-12 pt-6 border-t border-zinc-200"><LegalNav current={slug} /></div>
      </div>
    </div>
  );
}
